import {
  EDITOR_MODE,
  useBlocksConfig,
  useEditorMode,
  useViewerState
} from "@pathwright/blocks-engine"
import useScrollToHashWithHashObserver from "@pathwright/ui/src/components/link/useScrollToHashWithHashObserver"
import ProgressBar from "@pathwright/ui/src/components/progress/ProgressBar"
import { LazyQuillEditor } from "@pathwright/ui/src/components/quill/LazyQuillEditor"
import { usePreload } from "@pathwright/ui/src/components/utils/preload"
import { useCallback } from "react"
import styled from "styled-components"
import ErrorOverlay from "../ui/ErrorOverlay"
import { DefaultLoadingWithTimeout } from "../utils/LoadingWithTimeout"
import BlockViewer from "./BlockViewer"
import EmptyContent from "./EmptyContent"

const Container = styled.div`
  position: relative;
`

function BlockViewerContainer({ block, blockIndex, onBlockChange }) {
  const editorMode = useEditorMode()
  const { saveBlock } = useViewerState()

  const handleChange = useCallback((id, data, trackProgress) => {
    // Optimistic user scoped update for Editor Preview mode
    if (onBlockChange && editorMode === EDITOR_MODE.EDIT) {
      onBlockChange(data, id)
    }
    saveBlock(id, data, trackProgress)
  }, [])

  return (
    <BlockViewer
      block={block}
      blockIndex={blockIndex}
      onChange={handleChange}
    />
  )
}

function BlocksViewer({ onBlockChange }) {
  const { blocks } = useViewerState()
  const { userID, mode: blocksMode, contextKey } = useBlocksConfig()

  // Sets an observer to detect when a Block is in view,
  // and set its ID as the hash in the URL
  // also scrolls to the block when the hash changes
  useScrollToHashWithHashObserver({
    selector: `.Step--is-active #blocks-content-viewer-${contextKey} .BlockWrap`,
    shouldScroll: blocksMode === "VIEW"
  })

  return blocks.map((block, i) => {
    return (
      <BlockViewerContainer
        key={`block-viewer-${block.contentID}:${block.id}-${userID}`}
        block={block}
        blockIndex={i}
        onBlockChange={onBlockChange}
      />
    )
  })
}

export const ContentViewer = ({ blocks: propsBlocks, onBlockChange }) => {
  const {
    blocks: viewerBlocks,
    completion,
    error,
    contentLoading
  } = useViewerState()
  const { contextKey } = useBlocksConfig()
  const blocks = propsBlocks || viewerBlocks

  // HACK: showing loader until QuillEditor is loaded for a more
  // seemless transition from loading to displaying content
  const [preloading] = usePreload({
    Component: LazyQuillEditor,
    preload: true
  })

  if ((preloading && process.env.NODE_ENV !== "test") || contentLoading) {
    return <DefaultLoadingWithTimeout />
  }

  if (error) {
    return (
      <div key={"content-viewer"}>
        <Container>
          {error ? <ErrorOverlay error={error} /> : null}
          {children}
        </Container>
      </div>
    )
  }

  return (
    <div key={"content-viewer"} id={`blocks-content-viewer-${contextKey}`}>
      <ProgressBar progress={completion.progress} />
      {!blocks.length ? (
        <EmptyContent />
      ) : (
        <BlocksViewer onBlockChange={onBlockChange} />
      )}
    </div>
  )
}

// const ContentViewerContainer = props => {
//   const { isLocal } = useContentContext()
//   const syncerState = useSyncer(isLocal)

//   // If ContentViewer is rendered by ContentEditor, use the editor blocks
//   const { blocks } = useEditorBlocks()

//   return (
//     <ViewerStateProvider {...syncerState} {...props} editorBlocks={blocks}>
//       <ContentViewer />
//     </ViewerStateProvider>
//   )
// }

// export default ContentViewerContainer
